import { useMemo } from 'react';

import type { SitePreview } from '@/api/fe-api/domain/entities/site';
import { GetAllSites } from '@/api/fe-api/view/queries/get-all-sites';
import { FieldSelect } from '@/components/looker/field-select';
import { useMe } from '@/hooks/use-me';
import { singular } from '@/utils/format/label';

export type SiteFilterProps = {
  value?: string;
  onChange: (value?: string) => void;
  // Optional BE-provided options (preferred when present)
  options?: { id: string; label: string }[];
  isLoading?: boolean;
};

export function SiteFilter({
  value,
  onChange,
  options,
  isLoading = false,
}: SiteFilterProps) {
  const me = useMe();
  const listSites = GetAllSites.useQuery();

  // Compute selected label based on provided options or fallback to site list
  const selectedLabel = useMemo<string | undefined>(() => {
    if (options && options.length > 0 && value) {
      const match = options.find((o) => o.id === value);
      return match?.label;
    }
    const selectedSite: SitePreview | undefined = listSites.data?.find(
      ({ fullFrontendId }) => fullFrontendId === value,
    );
    return selectedSite?.name;
  }, [options, value, listSites.data]);

  // Normalize options to FieldSelect shape
  const selectOptions = useMemo(
    () =>
      (options && options.length > 0
        ? options.map((o) => ({ value: o.id, label: o.label }))
        : (listSites.data ?? []).map(({ fullFrontendId, id }) => ({
            label: fullFrontendId,
            value: id,
          }))) as { value: string; label?: string }[],
    [options, listSites.data],
  );

  const isComponentLoading =
    isLoading || (listSites.isLoading && listSites.fetchStatus !== 'idle');

  return (
    <FieldSelect
      data-testid="system-logs-site-filter"
      label={singular(me.config.group.labels.name)}
      placeholder={`Filter by ${singular(me.config.group.labels.name)}`}
      value={selectedLabel}
      options={selectOptions}
      onChange={onChange}
      isFilterable={true}
      isClearable={true}
      isLoading={isComponentLoading}
      disabled={isComponentLoading}
    />
  );
}
